import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import Modal from './Modal';
import DatasheetPreview from './DatasheetPreview';
import USDatasheetPreview from './USDatasheetPreview';
import { useRegion } from '../region';

export default function CoreRangeView({ toast, canManage, openInEditor }) {
  const region = useRegion();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [preview, setPreview] = useState(null); // product
  const [pdfUrl, setPdfUrl] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(async () => {
    const r = await api.listProducts({ scope: 'core', limit: 500 });
    setItems(r.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let url = null;
    setPdfUrl(null);
    if (preview && preview.ds_file_id) {
      api.getLegacyObjectUrl(preview.ds_file_id).then(u => { url = u; setPdfUrl(u); })
        .catch(() => toast('Could not load the datasheet file', true));
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [preview]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = (items || []).filter(p => {
    if (!q.trim()) return true;
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    const t = [p.name, p.code, p.family].join(' ').toLowerCase();
    return terms.every(x => t.includes(x));
  });
  const families = [];
  const byFam = {};
  shown.forEach(p => {
    const f = (p.family || '').trim() || 'No family';
    if (!byFam[f]) { byFam[f] = []; families.push(f); }
    byFam[f].push(p);
  });
  families.sort((a, b) => (a === 'No family') - (b === 'No family') || a.localeCompare(b));
  const searching = !!q.trim();

  async function attachPdf(p, file) {
    if (!file) return;
    try {
      const meta = await api.uploadLegacy(file, (p.code || p.name || 'datasheet') + ' — ' + file.name, 'core-range');
      await api.updateProduct(p.id, { ds_file_id: meta.id });
      toast('Datasheet PDF attached'); load();
    } catch (e) { toast(e?.response?.data?.detail || 'Attach failed', true); }
  }
  async function del() {
    const p = confirmDel; setConfirmDel(null);
    try { await api.deleteProduct(p.id); toast('Removed from Core Range'); load(); }
    catch (e) { toast(e?.response?.data?.detail || 'Delete failed', true); }
  }

  return (
    <div className="pc-view" data-testid="core-range-view">
      <div className="pc-head">
        <div>
          <h2>Core Range</h2>
          <p className="pc-sub">Browse and search the standard product range. Items link to NetSuite and carry a datasheet (uploaded PDF or built in the Editor).</p>
        </div>
        {canManage && <button className="btn btn-primary" data-testid="core-add-btn" onClick={() => setAddOpen(true)}>＋ Add to Core Range</button>}
      </div>
      <input className="input" data-testid="core-search" style={{ maxWidth: 420, marginBottom: 14 }}
        placeholder="Search name, code or family…" value={q} onChange={e => setQ(e.target.value)} />
      {items === null && <div className="empty-note">Loading…</div>}
      {items && shown.length === 0 && <div className="empty-note" data-testid="core-empty">{q ? 'No matches.' : 'Core Range is empty — add the first item.'}</div>}
      {families.map(fam => {
        const isCollapsed = !searching && collapsed[fam];
        return (
          <section key={fam} style={{ marginBottom: 14 }}>
            <button className="btn" data-testid={'core-fam-' + fam.replace(/\s+/g, '-')}
              style={{ width: '100%', justifyContent: 'space-between', display: 'flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', fontWeight: 800, padding: '10px 14px', marginBottom: isCollapsed ? 0 : 10 }}
              onClick={() => setCollapsed(c => ({ ...c, [fam]: !c[fam] }))}>
              <span>{isCollapsed ? '▸' : '▾'} {fam}</span>
              <span className="ps-muted" style={{ fontWeight: 500 }}>{byFam[fam].length} item{byFam[fam].length !== 1 ? 's' : ''}</span>
            </button>
            {!isCollapsed && (
              <div className="dash-cols" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', display: 'grid', gap: 12 }}>
                {byFam[fam].map(p => (
          <div key={p.id} className="dash-card" data-testid={'core-item-' + p.id} style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div>
                <strong>{p.name || '(Untitled)'}</strong>
                <div className="ps-muted" style={{ fontSize: 12 }}>{[p.code, p.family].filter(Boolean).join(' · ')}</div>
              </div>
              {canManage && <button className="btn btn-sm btn-danger" data-testid={'core-del-' + p.id} onClick={() => setConfirmDel(p)} title="Remove">✕</button>}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
              {p.netsuite_item_id
                ? <span className="pc-ds-pill found" title={'NetSuite item ' + p.netsuite_item_id}>✓ NetSuite linked</span>
                : <span className="pc-ds-pill missing">Not linked to NetSuite</span>}
              {p.ds_file_id
                ? <span className="pc-ds-pill found">PDF datasheet</span>
                : (Object.keys(p.data || {}).length > 2
                  ? <span className="pc-ds-pill found">Editor datasheet</span>
                  : <span className="pc-ds-pill missing">No datasheet yet</span>)}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="btn btn-sm" data-testid={'core-preview-' + p.id} onClick={() => setPreview(p)}>Preview</button>
              {canManage && !p.ds_file_id && Object.keys(p.data || {}).length > 2 && (
                <button className="btn btn-sm" data-testid={'core-edit-' + p.id} onClick={() => openInEditor(p.id)}>Open in Editor</button>
              )}
              {canManage && (
                <label className="btn btn-sm" data-testid={'core-attach-' + p.id} title="Attach / replace the PDF datasheet">
                  {p.ds_file_id ? 'Replace PDF' : 'Attach PDF'}
                  <input type="file" accept="application/pdf" style={{ display: 'none' }}
                    onChange={e => { attachPdf(p, e.target.files[0]); e.target.value = ''; }} />
                </label>
              )}
            </div>
          </div>
        ))}
              </div>
            )}
          </section>
        );
      })}

      {preview && (
        <div className="modal-overlay" data-testid="core-preview-modal" onClick={() => setPreview(null)}>
          <div className="modal" style={{ maxWidth: 900, width: '92vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>{preview.name} <span className="ps-muted" style={{ fontSize: 13 }}>{preview.code}</span></h3>
              <button className="modal-close" data-testid="core-preview-close" onClick={() => setPreview(null)}>×</button>
            </div>
            {preview.ds_file_id
              ? (pdfUrl ? <iframe title="Datasheet" src={pdfUrl} style={{ width: '100%', height: '75vh', border: '1px solid var(--border)' }} />
                : <div className="empty-note">Loading PDF…</div>)
              : Object.keys(preview.data || {}).length > 2
                ? <div style={{ transform: 'scale(0.85)', transformOrigin: 'top left' }}>
                    {region === 'US' ? <USDatasheetPreview sheet={preview.data} pageId={'core-pv'} /> : <DatasheetPreview sheet={preview.data} pageId={'core-pv'} />}
                  </div>
                : <div className="empty-note">No datasheet attached yet{canManage ? ' — use "Attach PDF" or build one in the Editor.' : '.'}</div>}
          </div>
        </div>
      )}

      {addOpen && <AddCoreModal toast={toast} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} openInEditor={openInEditor} />}

      <Modal open={!!confirmDel} title="Remove from Core Range?" onClose={() => setConfirmDel(null)} testid="core-del-modal"
        actions={<>
          <button className="btn" onClick={() => setConfirmDel(null)}>Cancel</button>
          <button className="btn btn-danger-solid" data-testid="core-del-confirm" onClick={del}>Remove</button>
        </>}>
        <p>This permanently removes <strong>{confirmDel && (confirmDel.name || 'Untitled')}</strong> from the Core Range.</p>
      </Modal>
    </div>
  );
}

function AddCoreModal({ toast, onClose, onDone, openInEditor }) {
  const [mode, setMode] = useState('upload'); // upload | netsuite | editor
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [file, setFile] = useState(null);
  const [nsQ, setNsQ] = useState('');
  const [nsHits, setNsHits] = useState(null);
  const [nsPick, setNsPick] = useState(null);
  const [busy, setBusy] = useState(false);

  async function searchNs() {
    if (!nsQ.trim()) return;
    setBusy(true);
    try { const r = await api.nsSearch(nsQ.trim()); setNsHits(r.items || []); }
    catch (e) { toast(e?.response?.data?.detail || 'NetSuite search failed', true); }
    setBusy(false);
  }

  async function submit() {
    setBusy(true);
    try {
      let dsFileId = null;
      if (mode === 'upload') {
        if (!file || !(name.trim() || code.trim())) { toast('Add a name/code and pick a PDF', true); setBusy(false); return; }
        const meta = await api.uploadLegacy(file, (code.trim() || name.trim()) + ' — ' + file.name, 'core-range');
        dsFileId = meta.id;
      }
      if (mode === 'netsuite' && !nsPick) { toast('Search and pick a NetSuite item first', true); setBusy(false); return; }
      const finalName = name.trim() || (nsPick ? (nsPick.displayname || nsPick.itemid) : '');
      const finalCode = code.trim() || (nsPick ? nsPick.itemid : '');
      await api.createProduct({
        name: finalName || '(Untitled)', code: finalCode, scope: 'core',
        data: { name: finalName, code: finalCode },
        ds_file_id: dsFileId,
        netsuite_item_id: nsPick ? String(nsPick.id) : null,
      });
      toast('Added to Core Range' + (nsPick ? ' — linked to ' + nsPick.itemid : ''));
      onDone();
    } catch (e) { toast(e?.response?.data?.detail || 'Add failed', true); }
    setBusy(false);
  }

  const nsPicker = (
    <div style={{ marginTop: 10 }}>
      <label className="field-label">Link to an existing NetSuite item {mode === 'upload' ? '(optional)' : ''}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="input" data-testid="core-ns-search" placeholder="Search NetSuite by code…" value={nsQ}
          onChange={e => setNsQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') searchNs(); }} />
        <button className="btn btn-sm" data-testid="core-ns-search-btn" disabled={busy} onClick={searchNs}>Search</button>
      </div>
      {nsPick && <div className="pc-ds-pill found" style={{ marginTop: 6 }} data-testid="core-ns-picked">✓ {nsPick.itemid} — {nsPick.displayname || ''} <button className="btn btn-sm" onClick={() => setNsPick(null)}>✕</button></div>}
      {!nsPick && nsHits && (
        <div className="nsp-search-results" style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
          {nsHits.length === 0 && <div className="empty-note" style={{ border: 'none' }}>No NetSuite matches.</div>}
          {nsHits.map(h => (
            <button key={h.id} className="nsp-search-hit" data-testid={'core-ns-hit-' + h.id} onClick={() => setNsPick(h)}>
              <strong>{h.itemid}</strong><span>{h.displayname}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="modal-overlay" data-testid="core-add-modal" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Add to Core Range</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="si-focus-tabs" style={{ margin: '12px 0' }}>
          {[['upload', 'Upload PDF'], ['netsuite', 'From NetSuite code'], ['editor', 'Build in Editor']].map(([v, l]) => (
            <button key={v} className={'si-focus-tab' + (mode === v ? ' on' : '')} data-testid={'core-mode-' + v} onClick={() => setMode(v)}>{l}</button>
          ))}
        </div>
        {mode === 'editor' ? (
          <>
            <p className="modal-hint">Opens the Editor with a blank sheet — save it to the Core Range library when done.</p>
            <button className="btn btn-primary" data-testid="core-open-editor" onClick={() => { onClose(); openInEditor(null); }}>Open Editor →</button>
          </>
        ) : (
          <>
            <div className="field"><label className="field-label">Name</label>
              <input className="input" data-testid="core-add-name" value={name} onChange={e => setName(e.target.value)} placeholder={nsPick ? (nsPick.displayname || nsPick.itemid) : 'Product name'} /></div>
            <div className="field"><label className="field-label">Code</label>
              <input className="input" data-testid="core-add-code" value={code} onChange={e => setCode(e.target.value)} placeholder={nsPick ? nsPick.itemid : 'e.g. LPA-BTWP-KEN'} /></div>
            {mode === 'upload' && (
              <div className="field"><label className="field-label">Datasheet PDF</label>
                <input type="file" data-testid="core-add-file" accept="application/pdf" onChange={e => setFile(e.target.files[0])} /></div>
            )}
            {mode === 'netsuite' && <p className="modal-hint">Pick the NetSuite item — you can attach the PDF datasheet afterwards from the item's card.</p>}
            {nsPicker}
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" data-testid="core-add-submit" disabled={busy} onClick={submit}>{busy ? 'Adding…' : 'Add to Core Range'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
